import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import VisibilitySensor from 'react-visibility-sensor';
import _ from 'lodash';
import DropdownIcon from 'components/DropdownIcon';
import {
  OVERLAY_SHOW_SEASONS_TAB,
  OVERLAY_SHOW_CLIPS_TAB,
  OVERLAY_SHOW_MORE_TAB,
} from 'helpers/FilterHelper';
import { getUrlTarget } from 'helpers/ContentHelper';
import {
  scrollToElement,
  clearFetchNodeInterval,
  checkForDOMNode,
} from 'helpers/SeriesDetailHelpers';
import Dropdown from 'components/Dropdown';
import dropdownHeaderStyle from './DropdownHeader.module.scss';
import Style from './TabListMobile.module.scss';
import { getDropdownSeasonLabel } from '../../ContentDetailHelper';

class TabListMobile extends Component {
  constructor(props) {
    super(props);
    this.state = {
      moreTabItems: {
        items: [],
      },
      clippedTabItems: {},
      // currentFetchNode is used in checkForDOMNode and clearFetchNodeInterval
      // eslint-disable-next-line react/no-unused-state
      currentFetchNode: null,
    };
    this.checkForDOMNode = checkForDOMNode.bind(this);
    this.clearFetchNodeInterval = clearFetchNodeInterval.bind(this);
    this.detectClipping = this.detectClipping.bind(this);
  }

  getClipping() {
    const { clippedTabItems } = this.state;
    return _.some(clippedTabItems, item => item);
  }

  getSelectedDropdownItem = () => selected => {
    scrollToElement({
      slug: `${_.kebabCase(selected)}`,
      nodeFetcher: this.checkForDOMNode,
      duration: 10000,
      clearNodeFetcher: this.clearFetchNodeInterval,
      offsetTop: 250,
    });
  };

  closeAllDropdowns = () => {
    const { setOverlay } = this.props;
    const dropdowns = [OVERLAY_SHOW_SEASONS_TAB, OVERLAY_SHOW_CLIPS_TAB, OVERLAY_SHOW_MORE_TAB];
    _.each(dropdowns, dropdown => setOverlay(dropdown, false));
  };

  closeDropdown = overlay => () => {
    const { setOverlay } = this.props;
    return setOverlay(overlay, false);
  };

  detectClipping = (tabTitle, isVisible) => {
    const newClipping = !isVisible;
    const { clippedTabItems, moreTabItems } = this.state;
    const clippedTabItem = _.get(clippedTabItems, tabTitle);
    if (clippedTabItem !== newClipping) {
      this.setState(
        {
          clippedTabItems: {
            ...clippedTabItems,
            [_.camelCase(tabTitle)]: newClipping,
          },
        },
        () => {
          this.closeAllDropdowns();
          if (newClipping) {
            this.setState({
              moreTabItems: {
                items: _.uniqBy([...moreTabItems.items, ...this[tabTitle].props.items], 'id'),
              },
            });
          } else {
            this.setState({
              moreTabItems: {
                items: _.uniq(_.dropRight(moreTabItems.items, 1)),
              },
            });
          }
        },
      );
    }
  };

  handleGroupName = group => {
    const groupNames = {
      playlist: 'Clips',
    };

    return groupNames[group];
  };

  handleTabNameParsing = (tabName, tab) => {
    if (tabName) {
      if (tabName.indexOf('Season') > -1 || _.get(tab, 'tab.@type') === 'Season') {
        return 'seasons';
      }
    }
    return tabName;
  };

  handleTabTypeParsing = tab => {
    if (_.get(tab, 'onClick')) {
      return 'tabs';
    }

    return 'links';
  };

  openDropdown = overlay => () => {
    const { setOverlay } = this.props;
    return setOverlay(overlay, true);
  };

  createTabs(tab, isMoreTab) {
    if (_.isEmpty(tab)) return null;
    const {
      moreTabItems: { items: moreItems },
    } = this.state;
    const { tabs, pageId, selected } = this.props;
    const { items } = tab;
    const title = isMoreTab ? 'more' : _.camelCase(tab.name);
    const changeCb = isVisible => {
      this.detectClipping(title, isVisible);
    };
    const selectedItem = _.find(items, ({ linkTo }) => _.includes(linkTo, pageId));
    const isSeasonsTitle = title === 'seasons';
    const isActive = pageId ? Boolean(selectedItem) : isSeasonsTitle || tabs.length === 1;
    const defaultTitle = _.get(items, '0.parentName');
    const hideTab = !!_.find(moreItems, item => _.camelCase(item.name) === title);
    const containerClass = hideTab ? dropdownHeaderStyle.hidden : null;
    const dropdownLabel = getDropdownSeasonLabel(items, selected);
    const renderedTab = this.renderDropdown({
      items,
      title: dropdownLabel,
      isMoreTab,
      isActive,
      defaultTitle,
      containerClass,
    });
    const offset = { right: 30 };
    if (!_.isEmpty(moreItems)) offset.right = 110;

    return (
      <VisibilitySensor
        ref={el => {
          this[title] = el;
        }}
        key={title + _.get(tab, 'id')}
        offset={offset}
        onChange={changeCb}
        delayedCall
        active={title !== 'more'}
        items={items}
      >
        {renderedTab}
      </VisibilitySensor>
    );
  }

  transformTabs(items) {
    return _.reduce(
      items,
      (acc, tab) => {
        const { name: tabName, parentName } = tab;
        const name = this.handleTabNameParsing(parentName || tabName, tab);
        const type = this.handleTabTypeParsing(tab);

        if (!acc.tabs[name]) {
          acc.tabs[name] = { name };
          acc.tabs[name].type = type;
          acc.tabs[name].items = [];
        }

        acc.tabs[name].items.push(tab);

        return acc;
      },
      {
        tabs: {},
        more: {
          name: 'more',
          type: 'tabs',
          items: [],
        },
      },
    );
  }

  renderCurrentDropdowns() {
    const { tabs } = this.props;
    const tabItems = this.transformTabs(tabs).tabs;
    return _.map(tabItems, tabItem => this.createTabs(tabItem));
  }

  renderDropdown({ items, title, isMoreTab, isActive, defaultTitle, containerClass }) {
    const { overlay, selected } = this.props;
    const overlayType = {
      seasons: OVERLAY_SHOW_SEASONS_TAB,
    };
    if (!_.get(overlayType, title)) overlayType[title] = `overlay-${title}`;
    const dropdownOpen = overlay === _.get(overlayType, title);
    const dropdownLabel = getDropdownSeasonLabel(items, selected);
    const styleName = _.some(items, 'tab.headline') ? 'select' : title;
    return (
      <Dropdown
        className={containerClass}
        items={items}
        defaultText={isMoreTab ? _.capitalize(title) : defaultTitle || _.capitalize(title)}
        expandedHeaderCopy={dropdownHeaderStyle[styleName]}
        getIcon={onClick => <DropdownIcon onClick={onClick} opened={dropdownOpen} />}
        onOpenDropdown={this.openDropdown(overlayType[title])}
        onCloseDropdown={this.closeDropdown(overlayType[title])}
        expanded={dropdownOpen}
        theme={dropdownHeaderStyle}
        getSelected={this.getSelectedDropdownItem(title)}
        selected={dropdownLabel}
        preserveTitle={isMoreTab}
        useLabelDetail={title === 'clips'}
        displayResults
        showMobileCancel
        isActive={isActive}
        getUrlTarget={getUrlTarget}
      />
    );
  }

  render() {
    const {
      moreTabItems: { items: moreItems },
    } = this.state;
    const tabRowClasses = [Style.tabRow];
    if (!_.isEmpty(moreItems)) tabRowClasses.push(Style.moreSpacer);
    return (
      <div
        style={{ position: 'relative' }}
        ref={el => {
          this.tabRow = el;
        }}
      >
        <div className={classNames(tabRowClasses)}>{this.renderCurrentDropdowns()}</div>
        <div className={Style.moreTab}>{!!this.tabRow && this.getClipping()}</div>
      </div>
    );
  }
}

TabListMobile.defaultProps = {
  overlay: null,
  pageId: null,
  selected: null,
};

TabListMobile.propTypes = {
  overlay: PropTypes.string,
  setOverlay: PropTypes.func.isRequired,
  tabs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  pageId: PropTypes.string,
  selected: PropTypes.string,
};

TabListMobile.contextTypes = {
  location: PropTypes.shape({}),
};

export default TabListMobile;
